import "./address-book-row-detail.css";

import type {
	GridDetailRowProps,
	GridItemChangeEvent,
} from "@tql/ui-components-react/kendo/grid";
import { TextArea } from "@tql/ui-components-react/kendo/inputs";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { useCallback, useEffect, useMemo, useState } from "react";

import { LocationTypeOptions } from "~/features/quote/components/quote-details/components/pickup-delivery-details/components/location-type-options/location-type-options.tsx";
import { ServiceOptions } from "~/features/quote/components/quote-details/components/pickup-delivery-details/components/service-options/service-options.tsx";
import type { AddressBookItemSummary } from "~/shared/schemas/address-book-schema";
import type { SubGroupKey } from "~/shared/types/quote-details-types";

import { useAddressAccessorialSelection } from "../../hooks/use-address-accessorial-selection.ts";
import type { EditDescriptor } from "../../hooks/use-address-editing";

type AddressBookRowDetailProps = GridDetailRowProps & {
	edit: EditDescriptor;
	name: SubGroupKey;
	itemChange: (event: GridItemChangeEvent) => void;
	onCancelEdit: (dataItem: AddressBookItemSummary) => void;
	onEdit: (dataItem: AddressBookItemSummary) => void;
	onSave: (dataItem: AddressBookItemSummary) => void;
};

export const AddressBookRowDetail = (props: AddressBookRowDetailProps) => {
	const dataItem = props.dataItem as AddressBookItemSummary;
	const edit = props.edit;
	const { itemChange } = props;
	const isEditMode = useMemo(
		() =>
			dataItem.addressBookItemId in edit && edit[dataItem.addressBookItemId],
		[edit, dataItem.addressBookItemId],
	);
	const name = props.name;

	const {
		locationTypeOptions,
		serviceOptions,
		isOptionSelected,
		handleRadioSelectionChange,
		handleCombinedSelectionChange,
	} = useAddressAccessorialSelection({ dataItem, name, itemChange });

	// Get the first detail for accessorials and special instructions
	const detail = dataItem.details?.[0];

	// Local draft state for special instructions to prevent grid re-render from stealing focus
	const [specialInstructionsDraft, setSpecialInstructionsDraft] = useState(
		detail?.specialInstructions ?? "",
	);

	// Refresh draft when switching into edit mode or when the underlying data changes outside this component
	useEffect(() => {
		if (isEditMode) {
			setSpecialInstructionsDraft(detail?.specialInstructions ?? "");
		}
	}, [isEditMode, detail?.specialInstructions]);

	const commitSpecialInstructions = useCallback(() => {
		itemChange({
			dataItem,
			field: "specialInstructions",
			value: specialInstructionsDraft,
		} as GridItemChangeEvent);
	}, [dataItem, itemChange, specialInstructionsDraft]);

	return (
		<div
			className={`address-book-row-detail${isEditMode ? " row-detail-editing" : ""}`}
		>
			{dataItem.isLegacy ? (
				<div className={"address-book-row-detail-content"}>
					<div className={"address-book-row-detail-section"}>
						Accessorials and Special Instructions are not available for legacy
						addresses.
					</div>
				</div>
			) : (
				<>
					<div className={"address-book-row-detail-content"}>
						<div className={"address-book-row-detail-section"}>
							<b>Accessorials:</b>
							<LocationTypeOptions
								disabled={!isEditMode}
								handleRadioSelectionChange={handleRadioSelectionChange}
								isOptionSelected={isOptionSelected}
								locationTypeOptions={locationTypeOptions}
								name={name}
							/>
							<ServiceOptions
								disabled={!isEditMode}
								handleCombinedSelectionChange={handleCombinedSelectionChange}
								isOptionSelected={isOptionSelected}
								locationTypeOptions={locationTypeOptions}
								name={name}
								serviceOptions={serviceOptions}
							/>
						</div>
						<div className={"address-book-row-detail-section"}>
							<b>Special Instructions:</b>
							<div>
								{isEditMode ? (
									<TextArea
										onBlur={commitSpecialInstructions}
										onChange={(event) => {
											// Only update local draft to avoid remount focus loss
											setSpecialInstructionsDraft(event.value ?? "");
										}}
										rows={4}
										value={specialInstructionsDraft}
									/>
								) : (
									(detail?.specialInstructions ?? "")
								)}
							</div>
						</div>
					</div>
					<div className={"address-book-row-detail-buttons"}>
						{isEditMode ? (
							<Button
								onClick={() => {
									// Ensure latest draft committed before save
									commitSpecialInstructions();
									// Update the special instructions in the first detail
									const updatedDetails = [...dataItem.details];
									if (updatedDetails[0]) {
										updatedDetails[0] = {
											...updatedDetails[0],
											specialInstructions: specialInstructionsDraft,
										};
									}
									props.onSave({
										...dataItem,
										details: updatedDetails,
									});
								}}
								variant={"primary"}
							>
								Save
							</Button>
						) : (
							<Button
								onClick={() => props.onEdit(dataItem)}
								variant={"primary"}
							>
								Edit
							</Button>
						)}

						<Button onClick={() => props.onCancelEdit(dataItem)}>Cancel</Button>
					</div>
				</>
			)}
		</div>
	);
};
